import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Package, Heart, User, LogOut } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { ProductCard } from '@/components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, isAuthenticated, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const defaultTab = searchParams.get('tab') || 'profile';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchData();
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const [ordersRes, wishlistRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/wishlist`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setOrders(ordersRes.data);
      setWishlist(wishlistRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    try {
      await axios.delete(`${API}/wishlist/remove/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(prev => prev.filter(p => p.id !== productId));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove from wishlist');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="pt-24 md:pt-28 min-h-screen" data-testid="dashboard-page">
      <div className="container-custom">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Syne'] text-3xl md:text-4xl font-bold">My Account</h1>
            <p className="text-muted-foreground mt-1">Welcome back, {user?.first_name}</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="btn-outline rounded-none"
            data-testid="logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>

        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="bg-neutral-900 mb-8 flex-wrap h-auto">
            <TabsTrigger 
              value="profile" 
              className="data-[state=active]:bg-white data-[state=active]:text-black rounded-none flex items-center"
              data-testid="tab-profile"
            >
              <User size={16} className="mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger 
              value="orders"
              className="data-[state=active]:bg-white data-[state=active]:text-black rounded-none flex items-center"
              data-testid="tab-orders"
            >
              <Package size={16} className="mr-2" />
              Orders ({orders.length})
            </TabsTrigger>
            <TabsTrigger 
              value="wishlist"
              className="data-[state=active]:bg-white data-[state=active]:text-black rounded-none flex items-center"
              data-testid="tab-wishlist"
            >
              <Heart size={16} className="mr-2" />
              Wishlist ({wishlist.length})
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="border border-neutral-800 p-6 max-w-md">
              <h2 className="font-['Syne'] text-xl font-bold mb-6">Profile Information</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-muted-foreground text-sm">Name</p>
                  <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">Email</p>
                  <p className="font-medium">{user?.email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">Member Since</p>
                  <p className="font-medium">{user?.created_at ? formatDate(user.created_at) : 'N/A'}</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-24 bg-neutral-900 animate-pulse" />
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12 border border-neutral-800">
                <Package size={48} className="mx-auto text-neutral-700 mb-4" />
                <h3 className="font-['Syne'] text-xl font-bold mb-2">No orders yet</h3>
                <p className="text-muted-foreground mb-6">Start shopping to see your orders here</p>
                <Link to="/shop">
                  <Button className="btn-primary rounded-none" data-testid="start-shopping-btn">
                    Start Shopping
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="border border-neutral-800 p-6" data-testid={`order-${order.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Order ID</p>
                        <p className="font-mono text-sm">{order.id.slice(0, 8)}...</p>
                      </div>
                      <div className="mt-2 md:mt-0">
                        <p className="text-sm text-muted-foreground">Date</p>
                        <p className="text-sm">{formatDate(order.created_at)}</p>
                      </div>
                      <div className="mt-2 md:mt-0">
                        <p className="text-sm text-muted-foreground">Status</p>
                        <span className={`text-sm capitalize ${
                          order.order_status === 'confirmed' ? 'text-green-500' :
                          order.order_status === 'pending' ? 'text-yellow-500' : ''
                        }`}>
                          {order.order_status}
                        </span>
                      </div>
                      <div className="mt-2 md:mt-0">
                        <p className="text-sm text-muted-foreground">Total</p>
                        <p className="font-bold">{formatPrice(order.total)}</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {order.items?.slice(0, 4).map((item, idx) => (
                        <div key={idx} className="w-16 h-16 border border-neutral-800">
                          <img
                            src={item.product_image || 'https://via.placeholder.com/64'}
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                      {order.items?.length > 4 && (
                        <div className="w-16 h-16 border border-neutral-800 flex items-center justify-center text-sm text-muted-foreground">
                          +{order.items.length - 4}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Wishlist Tab */}
          <TabsContent value="wishlist">
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="aspect-[3/4] bg-neutral-900 animate-pulse" />
                ))}
              </div>
            ) : wishlist.length === 0 ? (
              <div className="text-center py-12 border border-neutral-800">
                <Heart size={48} className="mx-auto text-neutral-700 mb-4" />
                <h3 className="font-['Syne'] text-xl font-bold mb-2">Your wishlist is empty</h3>
                <p className="text-muted-foreground mb-6">Save items you love for later</p>
                <Link to="/shop">
                  <Button className="btn-primary rounded-none" data-testid="browse-products-btn">
                    Browse Products
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {wishlist.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToWishlist={handleRemoveFromWishlist}
                    isInWishlist={true}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
